import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { CustomTooltip } from '../sharedComponents/StyledTooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import RedditIcon from '@mui/icons-material/Reddit';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Box, Chip, ClickAwayListener, IconButton, OutlinedInput, InputAdornment, InputLabel, FormControl, TextField, Typography } from "@mui/material";

const RECIPIENT_PRESETS = ['uncle', 'dad', 'mom', 'cousin', 'coworker', 'boss', 'partner', 'buddy'];

// Read ?from= and ?to= from the URL once on mount so recipients of a
// personalized share link see the modal pre-filled for easy forwarding.
function readPersonalizationFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        const rawFrom = (params.get('from') || '').trim().slice(0, 30);
        const rawTo = (params.get('to') || '').trim().slice(0, 40);
        const toIsPreset = RECIPIENT_PRESETS.includes(rawTo.toLowerCase());
        return {
            from: rawFrom,
            toLabel: rawTo ? (toIsPreset ? rawTo.toLowerCase() : 'custom') : '',
            toCustom: rawTo && !toIsPreset ? rawTo : '',
        };
    } catch (_) {
        return { from: '', toLabel: '', toCustom: '' };
    }
}

export default function LinkShareModal({ link, modalOpen, handleCloseModal, idiotTypes }) {
    const [open, setOpen] = React.useState(false);
    const [copyTooltipOpen, setCopyTooltipOpen] = React.useState(false);
    const [modalTitle, setModalTitle] = React.useState('');
    const initialPersonalization = React.useRef(readPersonalizationFromUrl()).current;
    const [fromName, setFromName] = React.useState(initialPersonalization.from);
    const [toLabel, setToLabel] = React.useState(initialPersonalization.toLabel);
    const [toCustom, setToCustom] = React.useState(initialPersonalization.toCustom);

    const handleCopyToolTipClose = () => {
        setCopyTooltipOpen(false);
    }

    const formatString = () => {
        if (idiotTypes.length === 0) {
            return '';
        } else if (idiotTypes.length === 1) {
            return idiotTypes[0];
        } else if (idiotTypes.length === 2) {
            return `${idiotTypes[0]} or ${idiotTypes[1]}`;
        } else {
            const lastElement = idiotTypes[idiotTypes.length - 1];
            return `${idiotTypes.slice(0, idiotTypes.length - 1).join(', ')}, or ${lastElement}`;
        }
    }

    React.useEffect(() => {
        let idiotTypeString = formatString();
        if (!idiotTypeString) return;
        const startsWithVowel = /^[aeiou]/i.test(idiotTypeString);
        const article = startsWithVowel ? 'an' : 'a';
        const title = `You are likely sharing with ${article} ${idiotTypeString}`;
        setModalTitle(title);
    }, [idiotTypes])

    React.useEffect(() => {
        setOpen(modalOpen);
    }, [modalOpen]);

    // Build personalized URL from base link + from/to params
    const personalLink = React.useMemo(() => {
        if (!link) return '';
        const toValue = (toLabel === 'custom' ? toCustom : toLabel).trim();
        const fromValue = fromName.trim();
        if (!toValue && !fromValue) return link;
        const sep = link.includes('?') ? '&' : '?';
        const params = [];
        if (fromValue) params.push(`from=${encodeURIComponent(fromValue)}`);
        if (toValue) params.push(`to=${encodeURIComponent(toValue)}`);
        return `${link}${sep}${params.join('&')}`;
    }, [link, fromName, toLabel, toCustom]);

    const xShareText = React.useMemo(() => {
        const toValue = (toLabel === 'custom' ? toCustom : toLabel).trim();
        if (toValue) return `For my ${toValue} who won't shut up about EVs:`;
        return 'An EV claim, fact-checked with sources:';
    }, [toLabel, toCustom]);

    const handleClose = () => {
        setOpen(false);
        handleCloseModal(false);
    }

    const handleLinkCopy = () => {
        window.navigator.clipboard.writeText(personalLink);
        setCopyTooltipOpen(true);
        if (window.gtag) {
            window.gtag('event', 'share_copy_link', {
                has_recipient: !!(toLabel === 'custom' ? toCustom : toLabel),
                has_sender: !!fromName,
            });
        }
        setTimeout(() => {
          setCopyTooltipOpen(false);
        }, 1000);
    }

    const trackSocialShare = (platform) => {
        if (window.gtag) {
            window.gtag('event', 'share_social', {
                platform,
                has_recipient: !!(toLabel === 'custom' ? toCustom : toLabel),
                has_sender: !!fromName,
            });
        }
    };

    return (
        <React.Fragment>
            <Dialog
                data-track-context="share_modal"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    component: 'form',
                    sx: { borderRadius: 0, maxWidth: '520px' },
                }}
            >
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Typography fontWeight="700" fontSize="18px">
                        {modalTitle || 'Send this to an idiot'}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#555555', mt: 0.5 }}>
                        Start a new post or DM with a personalized link. (For replying in an existing thread, use "Reply to an idiot" instead.)
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box padding="6px">

                        {/* Who's it for */}
                        <Box sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111111', mb: 1 }}>
                                Who's it for?
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                                {RECIPIENT_PRESETS.map(preset => (
                                    <Chip
                                        key={preset}
                                        label={preset}
                                        onClick={() => setToLabel(toLabel === preset ? '' : preset)}
                                        sx={{
                                            backgroundColor: toLabel === preset ? '#111111' : '#fff',
                                            color: toLabel === preset ? '#fff' : '#111111',
                                            border: '1px solid #111111',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            textTransform: 'capitalize',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: toLabel === preset ? '#333333' : '#FFF6B8' },
                                        }}
                                    />
                                ))}
                                <Chip
                                    label="custom"
                                    onClick={() => setToLabel(toLabel === 'custom' ? '' : 'custom')}
                                    sx={{
                                        backgroundColor: toLabel === 'custom' ? '#111111' : '#fff',
                                        color: toLabel === 'custom' ? '#fff' : '#111111',
                                        border: '1px solid #111111',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: toLabel === 'custom' ? '#333333' : '#FFF6B8' },
                                    }}
                                />
                            </Box>
                            {toLabel === 'custom' && (
                                <TextField
                                    value={toCustom}
                                    onChange={(e) => setToCustom(e.target.value.slice(0, 40))}
                                    placeholder="e.g. brother-in-law, neighbor, boss..."
                                    size="small"
                                    fullWidth
                                    sx={{ mb: 1 }}
                                />
                            )}
                        </Box>

                        {/* From */}
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111111', mb: 1 }}>
                                Your name (optional)
                            </Typography>
                            <TextField
                                value={fromName}
                                onChange={(e) => setFromName(e.target.value.slice(0, 30))}
                                placeholder="So they know who called them out"
                                size="small"
                                fullWidth
                            />
                        </Box>

                        {/* Share link */}
                        <Box mt="20px">
                            <Box
                                component="form"
                                className='shareLinkForm'
                            >
                                <FormControl sx={{ m: 0, width: '100%' }} variant="outlined">
                                    <InputLabel htmlFor="shareLinkInput">Share with link</InputLabel>
                                    <OutlinedInput
                                        id="shareLinkInput"
                                        value={personalLink}
                                        readOnly
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <ClickAwayListener onClickAway={handleCopyToolTipClose}>
                                                    <div>
                                                        <CustomTooltip
                                                            onClose={handleCopyToolTipClose}
                                                            open={copyTooltipOpen}
                                                            disableFocusListener
                                                            disableHoverListener
                                                            disableTouchListener
                                                            title="Link copied to your clipboard"
                                                            slotProps={{
                                                            popper: {
                                                                disablePortal: true,
                                                            },
                                                            tooltip: {
                                                                sx: {
                                                                backgroundColor: "#111111"
                                                                }
                                                            },
                                                            arrow: {
                                                                sx: {
                                                                color: "#111111"
                                                                }
                                                            }
                                                            }}
                                                            placement='top'
                                                            arrow
                                                        >
                                                            <IconButton type="button" sx={{ p: '10px' }} onClick={handleLinkCopy}>
                                                            <ContentCopyIcon />
                                                            </IconButton>
                                                        </CustomTooltip>
                                                    </div>
                                                </ClickAwayListener>
                                            </InputAdornment>
                                        }
                                        label="Share with link"
                                    />
                                </FormControl>
                            </Box>
                        </Box>

                        <Box mt="16px" sx={{ display: "flex", justifyContent: "start" }}>
                            <Button className="shareSocialButtons" onClick={() => trackSocialShare('facebook')} href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(personalLink)}`} target='_blank'><FacebookRoundedIcon /></Button>
                            <Button className="shareSocialButtons" onClick={() => trackSocialShare('x')} href={`https://x.com/intent/tweet?url=${encodeURIComponent(personalLink)}&text=${encodeURIComponent(xShareText)}`} target='_blank'><XIcon /></Button>
                            <Button className="shareSocialButtons" onClick={() => trackSocialShare('reddit')} href={`https://www.reddit.com/submit?url=${encodeURIComponent(personalLink)}&title=${encodeURIComponent(xShareText)}`} target='_blank'><RedditIcon /></Button>
                            <Button className="shareSocialButtons" onClick={() => trackSocialShare('linkedin')} href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(personalLink)}`} target='_blank'><LinkedInIcon /></Button>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button sx={{ color: "#111111" }} onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
